import React, { useState } from "react";
import { CheckCircle, AlertCircle, Star, ChevronDown, ChevronUp } from "lucide-react";

type ReleaseNote = {
  version: string;
  date: string;
  notes: {
    type: "new" | "improvement" | "fix";
    text: string;
  }[];
};

const releaseNotes: ReleaseNote[] = [
  {
    version: "v2.1.0",
    date: "August 28, 2025",
    notes: [
      { type: "new", text: "Added support for bulk article upload" },
      { type: "improvement", text: "Improved dashboard performance" },
      { type: "fix", text: "Resolved issue with auto-publish toggle" },
    ],
  },
  {
    version: "v2.0.5",
    date: "August 10, 2025",
    notes: [
      { type: "new", text: "New analytics section in dashboard" },
      { type: "fix", text: "Fixed bug with article counts" },
    ],
  },
];

const typeConfig = {
  new: { icon: <Star className="w-4 h-4 text-blue-600" />, label: "What's New" },
  improvement: { icon: <AlertCircle className="w-4 h-4 text-amber-600" />, label: "Improvements" },
  fix: { icon: <CheckCircle className="w-4 h-4 text-green-600" />, label: "Fixes" },
};

export default function ReleaseNotes() {
  const [expandedIndex, setExpandedIndex] = useState(0); // top one expanded
  const [visibleCount, setVisibleCount] = useState(5); // show first 5 initially

  const toggleExpand = (idx: number) => {
    setExpandedIndex(expandedIndex === idx ? -1 : idx);
  };

  const loadMore = () => {
    setVisibleCount((prev) => Math.min(prev + 5, releaseNotes.length));
  };

  return (
    <div className="max-w-3xl mx-auto p-6">
      <h1 className="text-2xl font-bold mb-6">Release Notes</h1>
      <div className="space-y-4">
        {releaseNotes.slice(0, visibleCount).map((release, idx) => (
          <div
            key={idx}
            className="bg-white shadow rounded-2xl border border-gray-200"
          >
            {/* Header */}
            <div
              className="flex justify-between items-center px-6 py-4 cursor-pointer"
              onClick={() => toggleExpand(idx)}
            >
              <h2 className="text-lg font-semibold">{release.version}</h2>
              <div className="flex items-center space-x-3 text-gray-500 text-sm">
                <span>{release.date}</span>
                {expandedIndex === idx ? (
                  <ChevronUp className="w-5 h-5" />
                ) : (
                  <ChevronDown className="w-5 h-5" />
                )}
              </div>
            </div>

            {/* Notes (only if expanded) */}
            {expandedIndex === idx && (
              <div className="px-6 pb-4">
                <ul className="space-y-3">
                  {release.notes.map((note, i) => {
                    const { icon, label } = typeConfig[note.type];
                    return (
                      <li
                        key={i}
                        className="flex items-start space-x-3 text-gray-700"
                      >
                        <span className="mt-1">{icon}</span>
                        <div>
                          <p className="font-medium">{label}</p>
                          <p className="text-sm">{note.text}</p>
                        </div>
                      </li>
                    );
                  })}
                </ul>
              </div>
            )}
          </div>
        ))}

        {/* Load More Button */}
        {visibleCount < releaseNotes.length && (
          <div className="text-center mt-4">
            <button
              onClick={loadMore}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
            >
              Load More
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
